import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';
import { domainForSegment, NavigateConst } from 'common';
import { useTranslation } from 'react-i18next';
import { isAvMed } from 'context';
import GetStarted from './GetStarted';
import ChildPersonalInformation from './ChildPersonalInformation';
import ChildOnlyDrAccountSetup from './ChildOnlyDrAccountSetup';
import ActivationSuccess from './ActivationSuccess';
import DrAccountSetup from './DrAccountSetup';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const DrPersonalInformation = require('./DrPersonalInformation').default;

const getFormComponents = (pathname: string) => {
  const isChildOnlyReg = pathname === NavigateConst.childOnlyPlanDesignatedRep;
  const isSentara = !isAvMed();

  if (isChildOnlyReg) {
    return [
      ...(isSentara ? [GetStarted] : []),
      ChildPersonalInformation,
      DrPersonalInformation,
      ChildOnlyDrAccountSetup,
      ActivationSuccess,
    ];
  }

  return [DrPersonalInformation, DrAccountSetup, ActivationSuccess];
};

interface DrRegistrationStepsProps {
  page: number;
  setPage: (page: number) => void;
  formData: any;
  setFormData: (data: any) => void;
  setPlantypes: (types: string) => void;
  plantypes?: string;
  guest: boolean;
}

const DrRegistrationSteps = ({
  page,
  setPage,
  formData,
  setFormData,
  setPlantypes,
  plantypes,
  guest,
}: DrRegistrationStepsProps) => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { pageView } = useAdobeAnalytics();
  const formComponents = useMemo(() => getFormComponents(pathname), [pathname]);
  const CurrentComponent: any = formComponents[page];
  const isChildOnlyReg = pathname === NavigateConst.childOnlyPlanDesignatedRep;
  const isSentara = !isAvMed();

  const pageNames = useMemo(() => {
    const pages = isChildOnlyReg
      ? [
          ...(isSentara ? [t('common.registrationGetStarted')] : []),
          t('common.registrationParentChildInformation'),
          t('common.registrationPersonalInformation'),
          t('activation.accountSetup'),
          t('common.registrationActivationSuccess'),
        ]
      : [
          t('common.registrationPersonalInformation'),
          t('activation.accountSetup'),
          t('common.registrationActivationSuccess'),
        ];

    return pages.map(
      (label, index) => `Registration – Step ${index + 1}: ${label}`,
    );
  }, [isChildOnlyReg, isSentara, t]);

  useEffect(() => {
    if (!CurrentComponent || !pageNames[page]) {
      return;
    }

    pageView({
      segment: `${domainForSegment}${pathname}`,
      page: {
        pageName: pageNames[page],
        pageType: 'Registration',
        siteSection: 'registration',
        eventType: 'pageView',
      },
    });
  }, [CurrentComponent, page, pageNames, pageView, pathname]);

  if (!CurrentComponent) {
    return null;
  }

  return (
    <CurrentComponent
      setPage={setPage}
      page={page}
      formData={formData}
      setFormData={setFormData}
      setPlantypes={setPlantypes}
      plantypes={plantypes}
      guest={guest}
    />
  );
};

export default DrRegistrationSteps;
